import { parseSelector } from 'vega-event-selector';
import { parseExpression } from 'vega-expression';

import {
  estimateVegaLiteExpressionAllocation,
  VEGA_LITE_UNKNOWN_VALUE,
  type VegaLiteExpressionFields,
  type VegaLiteExpressionValueBounds,
} from './vega-lite-expression-allocation';
import { getVegaLiteNumericFormatIssue } from './vega-lite-numeric-format-policy';
import { VEGA_LITE_MAX_RESOURCE_COUNT } from './vega-lite-resource-limits';

/**
 * Expression-function policy for the pinned Vega 6 expression surface
 * (`vega-functions@6.1.3` `functionContext`).
 *
 * Vega evaluates expressions through the interpreter, so an expression cannot reach `Function` or
 * the network, but it can still ask Vega to allocate for it. `sequence(0, 1e9)` materializes the
 * whole array through `d3-array`'s `range`, `timeSequence`/`utcSequence` do the same through a time
 * interval, `pad(x, 1e9)` builds the string one character at a time in `vega-util`'s `repeat`, and
 * `replace(x, /a/g, y)` rewrites every match at once, so a nested global replacement grows the
 * string by a multiple on each pass. None of them is bounded by the row limits Neptune enforces on
 * the query side.
 *
 * Regex literals and `regexp`/`test` can run synchronous unbounded matching. `gradient` materializes
 * scale ticks, so its optional count must be a small integer literal, not a runtime expression.
 *
 * The data-access functions are refused for a different reason: `data`, `indata`, `setdata` and
 * `modify` read or rewrite dataflow datasets, and `treePath`/`treeAncestors` walk them, so they step
 * outside the single Neptune dataset the widget is allowed to show.
 */
export type VegaLiteExpressionFunctionReason = 'allocation' | 'dataAccess' | 'regularExpression';

export const VEGA_LITE_FORBIDDEN_EXPRESSION_FUNCTIONS: Readonly<
  Record<string, VegaLiteExpressionFunctionReason>
> = {
  data: 'dataAccess',
  indata: 'dataAccess',
  modify: 'dataAccess',
  pad: 'allocation',
  regexp: 'regularExpression',
  replace: 'allocation',
  sequence: 'allocation',
  setdata: 'dataAccess',
  test: 'regularExpression',
  timeSequence: 'allocation',
  treeAncestors: 'dataAccess',
  treePath: 'dataAccess',
  utcSequence: 'allocation',
};

/**
 * Own keys only: a prototype member such as `constructor` must not look like a forbidden function.
 */
const FORBIDDEN_FUNCTION_NAMES = new Set(Object.keys(VEGA_LITE_FORBIDDEN_EXPRESSION_FUNCTIONS));

export const VEGA_LITE_EXPRESSION_LIMITS = {
  maxDepth: 64,
  maxGradientCount: VEGA_LITE_MAX_RESOURCE_COUNT,
  maxNodes: 4096,
  maxTextLength: 64 * 1024,
  maxWork: 16_384,
} as const;

/**
 * `forbiddenFunction` names a call the policy refuses. The two `unparsable` findings mean Vega
 * itself could not read the text; such a value is reported instead of being let through, because a
 * string the policy cannot analyze is a string the policy cannot bound.
 */
export type VegaLiteExpressionFinding =
  | { kind: 'forbiddenFunction'; name: string }
  | { kind: 'complexityLimit'; detail: string }
  | { kind: 'allocationLimit'; detail: string }
  | { kind: 'gradientCount' }
  | { kind: 'numericFormat'; detail: string }
  | { kind: 'regularExpressionLiteral' }
  | { kind: 'unparsableEventSelector'; detail: string }
  | { kind: 'unparsableExpression'; detail: string };

const CALL_EXPRESSION = 'CallExpression';
const IDENTIFIER = 'Identifier';
const LITERAL = 'Literal';

/** The Vega event-stream source used when a selector omits one; only filters are inspected here. */
const DEFAULT_EVENT_SOURCE = 'view';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getStringProperty(record: UnknownRecord, key: string): string | undefined {
  const value = record[key];

  return typeof value === 'string' ? value : undefined;
}

function describeThrown(thrown: unknown): string {
  if (thrown instanceof Error) {
    return thrown.message;
  }

  if (typeof thrown === 'string') {
    return thrown;
  }

  return 'unknown error';
}

function dedupeFindings(
  findings: readonly VegaLiteExpressionFinding[],
): VegaLiteExpressionFinding[] {
  const seen = new Set<string>();

  return findings.filter((finding) => {
    let key: string = finding.kind;

    if (finding.kind === 'forbiddenFunction') {
      key += `:${finding.name}`;
    } else if ('detail' in finding) {
      key += `:${finding.detail}`;
    }

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);

    return true;
  });
}

type PolicyBudget = { nodes: number; work: number };

function complexityLimit(detail: string): VegaLiteExpressionFinding {
  return { detail, kind: 'complexityLimit' };
}

/**
 * Walks every parsed property without using the call stack. Flat arithmetic can produce a very
 * deep AST despite having no nested parentheses. Work includes scalar properties and is charged
 * before children are scheduled, bounding wide trees as well as nodes and depth. Event streams
 * and all their filters share one budget.
 */
function visitPolicyTree(
  value: unknown,
  budget: PolicyBudget,
  visit: (value: unknown, key?: string) => void,
): VegaLiteExpressionFinding | undefined {
  const pending: { depth: number; key?: string; value: unknown }[] = [{ depth: 0, value }];
  budget.work += 1;

  while (pending.length > 0) {
    if (budget.work > VEGA_LITE_EXPRESSION_LIMITS.maxWork) {
      return complexityLimit(
        `Analysis work must not exceed ${VEGA_LITE_EXPRESSION_LIMITS.maxWork} values`,
      );
    }

    const entry = pending.pop();

    if (entry === undefined) {
      break;
    }

    if (entry.depth > VEGA_LITE_EXPRESSION_LIMITS.maxDepth) {
      return complexityLimit(
        `Parsed depth must not exceed ${VEGA_LITE_EXPRESSION_LIMITS.maxDepth}`,
      );
    }

    const current = entry.value;
    visit(current, entry.key);

    if (!Array.isArray(current) && !isRecord(current)) {
      continue;
    }

    budget.nodes += 1;

    if (budget.nodes > VEGA_LITE_EXPRESSION_LIMITS.maxNodes) {
      return complexityLimit(
        `Parsed nodes must not exceed ${VEGA_LITE_EXPRESSION_LIMITS.maxNodes}`,
      );
    }

    const keys = Array.isArray(current) ? undefined : Object.keys(current);
    const childCount = Array.isArray(current) ? current.length : keys?.length ?? 0;
    budget.work += childCount;

    if (budget.work > VEGA_LITE_EXPRESSION_LIMITS.maxWork) {
      return complexityLimit(
        `Analysis work must not exceed ${VEGA_LITE_EXPRESSION_LIMITS.maxWork} values`,
      );
    }

    if (Array.isArray(current)) {
      for (let index = current.length - 1; index >= 0; index -= 1) {
        pending.push({ depth: entry.depth + 1, key: entry.key, value: current[index] });
      }
    } else {
      for (let index = (keys?.length ?? 0) - 1; index >= 0; index -= 1) {
        const key = keys?.[index];

        if (key !== undefined) {
          pending.push({ depth: entry.depth + 1, key, value: current[key] });
        }
      }
    }
  }

  return undefined;
}

/**
 * Vega's code generator resolves a call by the callee identifier and refuses member calls, so an
 * identifier in callee position is exactly the surface that reaches a forbidden implementation.
 * The parser has already decoded identifier escapes such as `s\u0065quence` and dropped grouping
 * parentheses such as `(sequence)(0, 1)`, which is why the check runs on the tree and not on text.
 */
function getCalleeName(node: UnknownRecord): string | undefined {
  if (getStringProperty(node, 'type') !== CALL_EXPRESSION) {
    return undefined;
  }

  const callee = node.callee;

  if (!isRecord(callee) || getStringProperty(callee, 'type') !== IDENTIFIER) {
    return undefined;
  }

  return getStringProperty(callee, 'name');
}

function hasBoundedGradientCount(node: UnknownRecord): boolean {
  const args = node.arguments;

  if (!Array.isArray(args)) {
    return false;
  }

  if (args.length < 4) {
    return true;
  }

  const count = args[3];

  return (
    isRecord(count) &&
    getStringProperty(count, 'type') === LITERAL &&
    typeof count.value === 'number' &&
    Number.isFinite(count.value) &&
    Number.isInteger(count.value) &&
    count.value >= 1 &&
    count.value <= VEGA_LITE_EXPRESSION_LIMITS.maxGradientCount
  );
}

function getNumericFormatFinding(node: UnknownRecord): VegaLiteExpressionFinding | undefined {
  const specifier = Array.isArray(node.arguments) ? node.arguments[1] : undefined;
  const value =
    isRecord(specifier) && getStringProperty(specifier, 'type') === LITERAL
      ? specifier.value
      : undefined;
  const issue = getVegaLiteNumericFormatIssue(value);

  return issue ? { kind: 'numericFormat', detail: issue.message } : undefined;
}

/**
 * Parses a Vega expression with the same parser the pinned runtime uses and reports the forbidden
 * calls and regex literals it contains. Strings and property names that merely mention a forbidden
 * function stay allowed.
 */
function analyzeExpression(
  expression: string,
  budget: PolicyBudget,
): { ast?: unknown; findings: VegaLiteExpressionFinding[] } {
  if (expression.length > VEGA_LITE_EXPRESSION_LIMITS.maxTextLength) {
    return {
      findings: [
        complexityLimit(
          `Expression text must not exceed ${VEGA_LITE_EXPRESSION_LIMITS.maxTextLength} characters`,
        ),
      ],
    };
  }

  let ast: unknown;

  try {
    ast = parseExpression(expression);
  } catch (error) {
    return { findings: [{ detail: describeThrown(error), kind: 'unparsableExpression' }] };
  }

  const findings: VegaLiteExpressionFinding[] = [];

  const limit = visitPolicyTree(ast, budget, (node) => {
    if (!isRecord(node)) {
      return;
    }

    if (getStringProperty(node, 'type') === LITERAL && isRecord(node.regex)) {
      findings.push({ kind: 'regularExpressionLiteral' });
    }

    const name = getCalleeName(node);

    if (name !== undefined && FORBIDDEN_FUNCTION_NAMES.has(name)) {
      findings.push({ kind: 'forbiddenFunction', name });
    }

    if (name === 'gradient' && !hasBoundedGradientCount(node)) {
      findings.push({ kind: 'gradientCount' });
    }

    if (name === 'format') {
      const finding = getNumericFormatFinding(node);

      if (finding) {
        findings.push(finding);
      }
    }
  });

  if (limit) {
    findings.push(limit);
  }

  return { ast, findings: dedupeFindings(findings) };
}

export function analyzeVegaLiteExpression(expression: string): VegaLiteExpressionFinding[] {
  return analyzeExpression(expression, { nodes: 0, work: 0 }).findings;
}

export function analyzeVegaLiteCalculateExpression(
  expression: string,
  fields: VegaLiteExpressionFields = new Map(),
): VegaLiteExpressionAllocationAnalysis {
  return analyzeVegaLiteExpressionAllocation(expression, fields, true);
}

export function analyzeVegaLiteTupleExpression(
  expression: string,
  fields: VegaLiteExpressionFields = new Map(),
): VegaLiteExpressionAllocationAnalysis {
  return analyzeVegaLiteExpressionAllocation(expression, fields, false);
}

type VegaLiteExpressionAllocationAnalysis = {
  allocation: number;
  findings: VegaLiteExpressionFinding[];
  value: VegaLiteExpressionValueBounds;
};

function analyzeVegaLiteExpressionAllocation(
  expression: string,
  fields: VegaLiteExpressionFields,
  retainResult: boolean,
): VegaLiteExpressionAllocationAnalysis {
  const { ast, findings } = analyzeExpression(expression, { nodes: 0, work: 0 });

  if (findings.length > 0) {
    return { allocation: 0, findings, value: VEGA_LITE_UNKNOWN_VALUE };
  }

  const result = estimateVegaLiteExpressionAllocation(ast, fields, { retainResult });

  return result.status === 'bounded'
    ? { allocation: result.allocation, findings, value: result.value }
    : {
        allocation: 0,
        findings: [{ kind: 'allocationLimit', detail: result.detail }],
        value: VEGA_LITE_UNKNOWN_VALUE,
      };
}

/**
 * Applies the expression policy to a Vega event selector such as `click[datum.y > 1]`. Vega-Lite
 * hands these strings to `vega-event-selector`, so a filter inside the brackets is compiled and run
 * on interaction and has to be analyzed like any other expression. The unfiltered forms - `click`,
 * `mousemove`, `wheel!`, `[mousedown, window:mouseup] > window:mousemove!` - carry no expression
 * and stay allowed.
 */
export function analyzeVegaLiteEventSelector(selector: string): VegaLiteExpressionFinding[] {
  if (selector.length > VEGA_LITE_EXPRESSION_LIMITS.maxTextLength) {
    return [
      complexityLimit(
        `Event selector text must not exceed ${VEGA_LITE_EXPRESSION_LIMITS.maxTextLength} characters`,
      ),
    ];
  }

  let streams: unknown;

  try {
    streams = parseSelector(selector, DEFAULT_EVENT_SOURCE);
  } catch (error) {
    return [{ detail: describeThrown(error), kind: 'unparsableEventSelector' }];
  }

  const budget = { nodes: 0, work: 0 };
  const filters: string[] = [];
  const limit = visitPolicyTree(streams, budget, (value, key) => {
    if (key === 'filter' && typeof value === 'string') {
      filters.push(value);
    }
  });

  if (limit) {
    return [limit];
  }

  const findings: VegaLiteExpressionFinding[] = [];

  for (const filter of filters) {
    const filterFindings = analyzeExpression(filter, budget).findings.map(
      (finding): VegaLiteExpressionFinding => {
        if (finding.kind === 'unparsableExpression') {
          return {
            detail: `the filter "${filter}" is not a valid expression (${finding.detail})`,
            kind: 'unparsableEventSelector',
          };
        }

        return finding;
      },
    );

    findings.push(...filterFindings);

    if (filterFindings.some((finding) => finding.kind === 'complexityLimit')) {
      break;
    }
  }

  return dedupeFindings(findings);
}

function formatNames(names: readonly string[]): string {
  return names.map((name) => `"${name}"`).join(', ');
}

function getForbiddenNames(
  findings: readonly VegaLiteExpressionFinding[],
  reason: VegaLiteExpressionFunctionReason,
): string[] {
  return findings.flatMap((finding) =>
    finding.kind === 'forbiddenFunction' &&
    VEGA_LITE_FORBIDDEN_EXPRESSION_FUNCTIONS[finding.name] === reason
      ? [finding.name]
      : [],
  );
}

/**
 * One message per reason so the editor can tell an author why a value was refused instead of
 * repeating a generic "unsupported expression".
 */
export function describeVegaLiteExpressionFindings(
  findings: readonly VegaLiteExpressionFinding[],
  datasetName: string,
): string[] {
  const allocation = getForbiddenNames(findings, 'allocation');
  const dataAccess = getForbiddenNames(findings, 'dataAccess');
  const regularExpression = getForbiddenNames(findings, 'regularExpression');
  const messages: string[] = [];

  if (allocation.length > 0) {
    messages.push(
      `Expression functions that allocate unbounded data are not supported: ${formatNames(
        allocation,
      )}. Derive values from the chart rows instead.`,
    );
  }

  if (dataAccess.length > 0) {
    messages.push(
      `Expression functions that read or modify datasets are not supported: ${formatNames(
        dataAccess,
      )}. Chart data always comes from the "${datasetName}" dataset.`,
    );
  }

  if (
    regularExpression.length > 0 ||
    findings.some((finding) => finding.kind === 'regularExpressionLiteral')
  ) {
    messages.push(
      'Regular expressions (literals, "regexp" and "test") are not supported because matching cannot be bounded. Use string operations such as indexof or split with a string separator instead.',
    );
  }

  if (findings.some((finding) => finding.kind === 'gradientCount')) {
    messages.push(
      `The "gradient" count must be omitted or be a finite integer literal from 1 to ${VEGA_LITE_EXPRESSION_LIMITS.maxGradientCount}. Computed counts are not supported.`,
    );
  }

  findings.forEach((finding) => {
    if (finding.kind === 'allocationLimit') {
      messages.push(`${finding.detail}. Use a bounded label or explicitly numeric calculation.`);
    }

    if (finding.kind === 'numericFormat') {
      messages.push(`The "format" specifier is not supported. ${finding.detail}`);
    }

    if (finding.kind === 'complexityLimit') {
      messages.push(
        `${finding.detail}. Simplify the expression or event selector before rendering.`,
      );
    }

    if (finding.kind === 'unparsableExpression') {
      messages.push(
        `This value must be a Vega expression the renderer can read. Fix the syntax: ${finding.detail}.`,
      );
    }

    if (finding.kind === 'unparsableEventSelector') {
      messages.push(
        `This value must be a Vega event selector the renderer can read, such as "click" or "[mousedown, window:mouseup] > window:mousemove". Fix the syntax: ${finding.detail}.`,
      );
    }
  });

  return Array.from(new Set(messages));
}
